<template>
  <!-- Modal Structure -->
  <div v-if="showModal" class="fixed w-full h-full top-0 left-0 flex items-center justify-center z-50">
    <div class="absolute w-full h-full bg-gray-900 opacity-50" @click="closeModal"></div>
    <div class="relative bg-white rounded-lg shadow dark:bg-gray-700 max-w-5xl w-auto mx-4">
      <!-- Modal header -->
      <div class="flex items-center justify-between p-4 md:p-5 border-b rounded-t dark:border-gray-600">
        <h3 class="text-xl font-semibold text-gray-900 dark:text-white">Available Assets</h3>
        <button @click="closeModal" class="text-red-500 text-2xl">&times;</button>
      </div>

      <!-- Modal body -->
      <div class="p-4">
        <!-- Search Input -->
        <div class="mb-4">
          <input v-model="search" @input="fetchAssets" type="text" class="border border-gray-300 p-2 rounded w-full" placeholder="Search assets..." />
        </div>

        <!-- Limit Selector -->
        <div class="mb-4">
          <label for="limit" class="mr-2">Show:</label>
          <select v-model="limit" @change="fetchAssets" id="limit" class="border border-gray-300 p-2 rounded">
            <option v-for="option in limitOptions" :key="option" :value="option">{{ option }}</option>
          </select>
        </div>

        <!-- Assets Table -->
        <div v-if="assets.length > 0" class="overflow-y-auto max-h-96">
          <table class="min-w-full bg-white">
            <thead>
              <tr class="text-left font-bold bg-gray-100">
                <th class="px-6 py-3">
                  <input type="checkbox" @change="toggleSelectAll($event)" class="large-checkbox" />
                </th>
                <th class="px-6 py-3">Code</th>
                <th class="px-6 py-3">Serial Number</th>
                <th class="px-6 py-3">Type</th>
                <th class="px-6 py-3">Brand</th>
                <th class="px-6 py-3">Category</th>
                <th class="px-6 py-3">Join Date</th>
                <th class="px-6 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="asset in assets" :key="asset.id" class="hover:bg-gray-100">
                <td class="border-t px-6 py-4">
                  <input type="checkbox" :value="asset" v-model="selectedAssets" class="large-checkbox" />
                </td>
                <td class="border-t px-6 py-4 cursor-pointer" @click="toggleCheckbox(asset)">
                  {{ asset.code }}
                </td>
                <td class="border-t px-6 py-4 cursor-pointer" @click="toggleCheckbox(asset)">{{ asset.serial_number }}</td>
                <td class="border-t px-6 py-4 cursor-pointer" @click="toggleCheckbox(asset)">{{ asset.type }}</td>
                <td class="border-t px-6 py-4 cursor-pointer" @click="toggleCheckbox(asset)">{{ asset.brand }}</td>
                <td class="border-t px-6 py-4 cursor-pointer" @click="toggleCheckbox(asset)">{{ asset.category }}</td>
                <td class="border-t px-6 py-4 cursor-pointer" @click="toggleCheckbox(asset)">{{ formatDate(asset.join_date) }}</td>
                <td class="border-t px-6 py-4 cursor-pointer" @click="toggleCheckbox(asset)">
                  <span :class="['px-2 py-1 rounded-full text-white', asset.status === 'available' ? 'bg-green-500' : 'bg-red-500']">
                    {{ asset.status }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="p-4 text-center text-gray-500">No assets available.</div>
      </div>

      <!-- Modal footer -->
      <div class="p-4 flex justify-between items-center border-t border-gray-200 rounded-b dark:border-gray-600">
        <!-- Pagination Component -->
        <PaginationModal v-if="paginationLinks && paginationLinks.pages && Object.keys(paginationLinks.pages).length > 0" :links="paginationLinks" @change-page="changePage" />

        <!-- Footer Buttons -->
        <div class="flex-shrink-0">
          <button v-if="selectedAssets.length > 0" type="button" @click="addSelectedAssets" class="mr-2 bg-green-700 text-white hover:bg-green-800 font-medium rounded-lg text-sm px-5 py-2.5 focus:outline-none focus:ring-4 focus:ring-green-300 dark:bg-green-600 dark:hover:bg-green-700 dark:focus:ring-green-800">Add Assets</button>
          <button type="button" @click="closeModal" class="bg-red-700 hover:bg-red-800 text-white font-medium rounded-lg text-sm px-5 py-2.5 focus:outline-none focus:ring-4 focus:ring-blue-300 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800">Close</button>
        </div>
      </div>
    </div>
  </div>
</template>

  <script>
import { ref, watch, nextTick } from 'vue'
import axios from 'axios'
import PaginationModal from '@/Shared/PaginationModal'

export default {
  components: {
    PaginationModal,
  },
  props: {
    showModal: Boolean,
  },
  setup(props, { emit }) {
    const assets = ref([])
    const paginationLinks = ref([])
    const search = ref('')
    const selectedAssets = ref([])
    const limit = ref(10) // Default limit value
    const limitOptions = [10, 25, 50, 100] // Limit options
    const modalContent = ref(null)

    const fetchAssets = async (customUrl = null) => {
      try {
        let url = customUrl && typeof customUrl === 'string' ? customUrl : route('asset.modal').toString()

        if (!url.startsWith('http')) {
          url = `${window.location.origin}${url}`
        }

        const params = {
          search: search.value,
          limit: limit.value, // Include limit in the request
        }

        const response = await axios.get(url, { params })

        const { data, paginationLinks: links, current_page, total_pages } = response.data

        assets.value = data || []
        paginationLinks.value = links || []
      } catch (error) {
        console.error('Error fetching assets:', error)
      }
    }

    const changePage = (url) => {
      fetchAssets(url)
    }

    watch(
      () => props.showModal,
      async (newValue) => {
        if (newValue) {
          await fetchAssets()
          nextTick(() => {
            if (modalContent.value) {
              modalContent.value.focus()
            }
          })
        }
      },
    )

    const toggleSelectAll = (event) => {
      if (event.target.checked) {
        selectedAssets.value = [...assets.value]
      } else {
        selectedAssets.value = []
      }
    }

    const toggleCheckbox = (asset) => {
      const index = selectedAssets.value.findIndex((a) => a.id === asset.id)
      if (index > -1) {
        selectedAssets.value.splice(index, 1)
      } else {
        selectedAssets.value.push(asset)
      }
    }

    const addSelectedAssets = () => {
      emit('assetsSelected', selectedAssets.value)
      closeModal()
    }

    const closeModal = () => {
      search.value = ''
      selectedAssets.value = []
      emit('close')
    }

    const formatDate = (dateStr) => {
      const date = new Date(dateStr)
      return date.toLocaleDateString()
    }

    return {
      assets,
      paginationLinks,
      search,
      selectedAssets,
      limit,
      limitOptions,
      fetchAssets,
      toggleSelectAll,
      toggleCheckbox,
      addSelectedAssets,
      closeModal,
      formatDate,
      modalContent,
      changePage,
    }
  },
}
</script>
